import { useApolloClient, useQuery } from '@apollo/client';
import { useFeatureGate } from '@statsig/react-bindings';
import { createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncClient } from 'twilio-sync';

import { logger } from '../datadog/logger';
import { sendLoggingEvents } from '../events';
import { Statsig } from '../statsig/StatsigProvider';
import { toast } from '../toasts';

/**
 * Dependencies of the application.
 */
export type AppDependencies = {
  SyncClient: typeof SyncClient;
  Statsig: typeof Statsig;
  logger: typeof logger;
  toast: typeof toast;
  sendLoggingEvents: typeof sendLoggingEvents;
  useNavigate: typeof useNavigate;
  useFeatureGate: typeof useFeatureGate;
  useQuery: typeof useQuery;
  useApolloClient: typeof useApolloClient;
};

export const DEFAULT_APP_DEPENDENCIES: AppDependencies = {
  SyncClient,
  Statsig,
  logger,
  toast,
  sendLoggingEvents,
  useNavigate,
  useFeatureGate,
  useQuery,
  useApolloClient,
};

/**
 * Used primarily in testing to override dependencies, but can also be used to
 * inject dependecies in other scenarios.
 *
 * ℹ️ Contexts will provide their default value if used outside of a
 * corresponding context provider.
 * */
export const AppDependenciesContext = createContext<AppDependencies>(DEFAULT_APP_DEPENDENCIES);

/**
 * @see {@link AppDependenciesContext}
 */
export const AppDependenciesProvider = AppDependenciesContext.Provider;
